import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Avatar,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import AddIcon from '@mui/icons-material/Add'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HistoryIcon from '@mui/icons-material/History'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import LogoutIcon from '@mui/icons-material/Logout'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { alpha } from '@mui/material/styles'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { TenantSettingsContext, UserContext } from '../../../App'
import { vatMultiplierForItem } from '../../../helpers/region'
import UserSessionIdleTimer from '../../../partials/UserSessionIdleTimer'
import ambunetLogo from '../../../assets/images/ambunetLogo.png'
import API from '../../../services/API'
import PoweredBySmall from '../../../components/Corporate/PoweredBySmall'
import DemoSwitchButton from '../../../components/demo/DemoSwitchButton'
import PortalChatDialog from '../components/PortalChatDialog'
import BigModal from '../../../components/Modals/BigModal'
import NewPTSBooking from '../NewBooking/NewBooking'
import PTSRiskAssessmentPDF from '../Patients/RiskAssessmentPDF'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import RiskAssessmentDialog from '../Patients/RiskAssessmentDialog'

const PTS_PORTAL_TERMS_VERSION = 'PTS_PORTAL_TERMS_2026_06_22'

const roleConfig = {
  Ward: {
    accent: '#7C3AED',
    accentDark: '#5B21B6',
    title: 'Ward Dashboard',
    subtitle: 'Request and view transport for the selected ward.',
  },
  Site: {
    accent: '#1F66CC',
    accentDark: '#1F3D7C',
    title: 'Site Dashboard',
    subtitle: 'Management-level view across the hospital site.',
  },
  Admin: {
    accent: '#00964C',
    accentDark: '#067647',
    title: 'Admin Dashboard',
    subtitle: 'Track bookings, journeys, reporting, and finance activity.',
  },
}

// Journey statuses grouped against the PTSJourney enum — the portal must
// never invent its own vocabulary (statuses like 'Booked'/'Delayed' don't
// exist and previously made these counts permanently zero)
const UPCOMING_STATUSES = ['Planned', 'Assigned', 'Crew Acknowledged']
const ACTIVE_STATUSES = [
  'Mobile',
  'On Scene',
  'Patient Onboard',
  'Transporting',
  'Arrived',
  'Handover Complete',
]

const isPlannedWithinNextHours = (journey, hours) => {
  if (!UPCOMING_STATUSES.includes(journey?.status)) return false
  // No time set (plannedAt falls back to creation time server-side) or a
  // pickup slot that has slipped into the past still counts as upcoming —
  // it's work that hasn't happened yet
  if (!journey?.plannedAt) return true
  const plannedAt = new Date(journey.plannedAt).getTime()
  const now = Date.now()
  return plannedAt <= now + hours * 60 * 60 * 1000
}

const isCompletedWithinPastHours = (journey, hours) => {
  if (!['Completed', 'No Show'].includes(journey?.status) || !journey?.completedAt) {
    return false
  }
  const completedAt = new Date(journey.completedAt).getTime()
  const now = Date.now()
  return completedAt <= now && completedAt >= now - hours * 60 * 60 * 1000
}

const isAlwaysVisibleWardStatus = (journey) =>
  journey?.status && !['Planned', 'Completed', 'No Show'].includes(journey.status)

const isSameDay = (firstDate, secondDate) =>
  firstDate.getFullYear() === secondDate.getFullYear() &&
  firstDate.getMonth() === secondDate.getMonth() &&
  firstDate.getDate() === secondDate.getDate()

const formatJourneyTime = (value, fallback = 'Any Time') => {
  if (!value) return fallback
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return fallback

  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })

  if (isSameDay(date, new Date())) return time

  return `${date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  })} ${time}`
}

const closedStatuses = ['Completed', 'Cancelled', 'No Show', 'Aborted']

const isClosedJourney = (journey) => closedStatuses.includes(journey?.status)

const getWardCounts = (journeys = []) => {
  const counts = new Map()
  journeys.forEach((journey) => {
    const ward = journey.ward || 'No ward'
    const current = counts.get(ward) || {
      ward,
      total: 0,
      active: 0,
      completed: 0,
      attention: 0,
    }
    current.total += 1
    if (isClosedJourney(journey)) current.completed += 1
    else current.active += 1
    if (isAlwaysVisibleWardStatus(journey)) current.attention += 1
    counts.set(ward, current)
  })
  return [...counts.values()].sort((a, b) => b.total - a.total)
}

const wardLabel = (ward) =>
  [
    ward?.name || ward?.wardName || ward?.label || ward?.title,
    ward?.internalLocation || ward?.wardLocation,
  ]
    .filter(Boolean)
    .join(' - ')

const getWardLabel = (ward) => wardLabel(ward)

const getInitials = (user) =>
  [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U'

const formatTermsAccepted = (access = {}) => {
  if (!access?.termsAcceptedAt || !access?.termsAcceptedVersion) {
    return 'Not accepted'
  }

  return `${access.termsAcceptedVersion} · ${new Date(
    access.termsAcceptedAt,
  ).toLocaleDateString('en-GB')}`
}

// One entry per PTSJourney status so every chip gets a meaningful colour
const statusStyles = {
  Planned: { color: '#7C3AED', bg: '#EDE9FE' },
  Assigned: { color: '#0E7490', bg: '#CFFAFE' },
  'Crew Acknowledged': { color: '#0E7490', bg: '#CFFAFE' },
  Mobile: { color: '#1F66CC', bg: '#DBEAFE' },
  'On Scene': { color: '#1F66CC', bg: '#DBEAFE' },
  'Patient Onboard': { color: '#1F66CC', bg: '#DBEAFE' },
  Transporting: { color: '#1F66CC', bg: '#DBEAFE' },
  Arrived: { color: '#1F66CC', bg: '#DBEAFE' },
  'Handover Complete': { color: '#067647', bg: '#DCFCE7' },
  Completed: { color: '#067647', bg: '#DCFCE7' },
  Cancelled: { color: '#B91C1C', bg: '#FEE2E2' },
  'No Show': { color: '#B45309', bg: '#FEF3C7' },
  Aborted: { color: '#B91C1C', bg: '#FEE2E2' },
}

// Booking-level review state, shown alongside the journey status so the
// hospital can see the outcome of their request (Pending = not yet reviewed)
const bookingStatusStyles = {
  Pending: { label: 'Awaiting review', color: '#92400E', bg: '#FEF3C7' },
  Confirmed: { label: 'Confirmed', color: '#067647', bg: '#DCFCE7' },
  Scheduled: { label: 'Scheduled', color: '#067647', bg: '#DCFCE7' },
  Rejected: { label: 'Rejected', color: '#B91C1C', bg: '#FEE2E2' },
  Cancelled: { label: 'Cancelled', color: '#B91C1C', bg: '#FEE2E2' },
}

// Live "crew inbound to the hospital" indicator — only present on rows where
// the server decided tracking applies (pickup at this hospital, patient not
// yet on board)
const getTrackingChip = (row) => {
  const tracking = row?.tracking
  if (!tracking) return null

  const minutesAgo = tracking.locationUpdatedAt
    ? Math.round(
        (Date.now() - new Date(tracking.locationUpdatedAt).getTime()) / 60000,
      )
    : null
  const freshness =
    minutesAgo === null
      ? 'No live position yet'
      : minutesAgo <= 1
        ? 'Position updated just now'
        : `Position updated ${minutesAgo} min ago`
  const who = [
    tracking.vehicle?.callSign,
    tracking.crew?.length ? tracking.crew.join(' & ') : null,
  ]
    .filter(Boolean)
    .join(' — ')

  if (tracking.state === 'atHospital') {
    return {
      label: '🚑 Crew at hospital',
      tooltip: [who, 'The crew has arrived at your hospital.']
        .filter(Boolean)
        .join(' · '),
      color: '#067647',
      bg: '#DCFCE7',
    }
  }
  if (tracking.state === 'enRoute') {
    const eta = tracking.etaSeconds
      ? ` · ~${Math.max(1, Math.round(tracking.etaSeconds / 60))} min`
      : ''
    return {
      label: `🚑 Crew en route${eta}`,
      tooltip: [who, freshness].filter(Boolean).join(' · '),
      color: '#1F66CC',
      bg: '#DBEAFE',
    }
  }
  return {
    label: '🚑 Crew assigned',
    tooltip: [who, 'The crew has acknowledged this journey.']
      .filter(Boolean)
      .join(' · '),
    color: '#0E7490',
    bg: '#CFFAFE',
  }
}

// The review chip only makes sense while the journey itself is still waiting
// to be picked up by the operator — once it's assigned, moving, or closed the
// journey status tells the real story (a Cancelled journey must never say
// "Awaiting review")
const getBookingChip = (row) => {
  const style = bookingStatusStyles[row?.bookingStatus]
  if (!style) return null
  if (row.bookingStatus === 'Pending') {
    return row.status === 'Planned' ? style : null
  }
  if (['Confirmed', 'Scheduled'].includes(row.bookingStatus)) {
    return row.status === 'Planned' ? style : null
  }
  return style
}

const NavButton = ({ item, active, accent, onClick }) => (
  <Button
    fullWidth
    startIcon={item.icon}
    onClick={onClick}
    sx={{
      justifyContent: 'flex-start',
      borderRadius: 1.5,
      px: 1.25,
      py: 1,
      color: active ? '#FFFFFF' : '#334155',
      bgcolor: active ? accent : 'transparent',
      textTransform: 'none',
      fontWeight: active ? 900 : 750,
      '&:hover': {
        bgcolor: active ? accent : alpha(accent, 0.08),
      },
    }}
  >
    {item.label}
  </Button>
)

const MetricCard = ({ icon, value, label, caption, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid rgba(36,47,64,0.1)',
      boxShadow: '0 14px 36px rgba(36,47,64,0.05)',
    }}
  >
    <Stack spacing={2} direction="row">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,
          bgcolor: alpha(color, 0.12),
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Stack flex={1}>
        <Typography variant="h5" fontWeight={950} lineHeight={1}>
          {value}
        </Typography>
        <Typography variant="body2" fontWeight={800}>
          {label}
        </Typography>
      </Stack>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Stack>
  </Paper>
)

const PortalRiskAssessmentButton = ({ row, previewOrganisationID }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [assessments, setAssessments] = useState([])
  const [formOpen, setFormOpen] = useState(false)
  const [editingAssessment, setEditingAssessment] = useState(null)
  const [saving, setSaving] = useState(false)
  const [localCount, setLocalCount] = useState(
    row?.riskAssessments?.count || 0,
  )
  const { user } = useContext(UserContext)
  const count = localCount

  const loadAssessments = async () => {
    if (!row?.patientId) return
    setOpen(true)
    setLoading(true)
    setError(false)

    try {
      const res = await API.get(
        previewOrganisationID
          ? `/pts/organisations/${previewOrganisationID}/portal-preview/patients/${row.patientId}/risk-assessments`
          : `/pts/portal/patients/${row.patientId}/risk-assessments`,
      )
      if (res.status === 200) {
        setAssessments(res.data || [])
        setLocalCount(res.data?.length ?? 0)
      } else {
        setError(true)
      }
    } catch (e) {
      console.error(e)
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const saveAssessment = async (assessment) => {
    if (!row?.patientId) return
    setSaving(true)
    try {
      const res = editingAssessment?._id
        ? await API.put(
            `/pts/portal/risk-assessments/${editingAssessment._id}`,
            assessment,
          )
        : await API.post(
            `/pts/portal/patients/${row.patientId}/risk-assessments`,
            assessment,
          )
      if ([200, 201].includes(res.status)) {
        setFormOpen(false)
        setEditingAssessment(null)
        if (!editingAssessment?._id) setLocalCount((prev) => prev + 1)
        if (open) await loadAssessments()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  // Preview mode is a co-ordinator looking through the hospital's eyes —
  // read-only, so no completion affordance there.
  const canComplete = !previewOrganisationID && !!row?.patientId

  if (!count && !canComplete) {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    )
  }

  if (!count) {
    // No assessment yet: a gently pulsing icon invites ward staff to
    // complete one right from the bookings list.
    return (
      <>
        <Tooltip title="No risk assessment yet - complete one now" arrow>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setEditingAssessment(null)
              setFormOpen(true)
            }}
            sx={{
              border: '1px solid',
              borderColor: 'primary.main',
              '@keyframes raPulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(59,91,219,0.35)' },
                '70%': { boxShadow: '0 0 0 9px rgba(59,91,219,0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(59,91,219,0)' },
              },
              animation: 'raPulse 2.4s ease-out infinite',
            }}
          >
            <MonitorHeartOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <RiskAssessmentDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSave={saveAssessment}
          patient={row.patientRecord}
          selectedAssessment={null}
          saving={saving}
          hideHospitalVisibility
        />
      </>
    )
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<VisibilityIcon />}
        onClick={loadAssessments}
        sx={{ textTransform: 'none', fontWeight: 800 }}
      >
        {count}
      </Button>
      <RiskAssessmentDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={saveAssessment}
        patient={row.patientRecord}
        selectedAssessment={editingAssessment}
        saving={saving}
        hideHospitalVisibility
      />
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Risk Assessments</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {loading && <LinearProgress />}
            {error && (
              <Alert severity="error">Unable to load risk assessments.</Alert>
            )}
            {!loading && !error && assessments.length === 0 && (
              <Alert severity="info">No risk assessments are visible.</Alert>
            )}
            {assessments.map((assessment) => (
              <Paper
                key={assessment._id}
                elevation={0}
                sx={{ p: 1.5, border: '1px solid rgba(36,47,64,0.1)' }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Box>
                    <Typography fontWeight={950}>
                      {assessment.assessmentType || 'Risk Assessment'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {assessment.visitDate
                        ? new Date(assessment.visitDate).toLocaleString('en-GB')
                        : new Date(assessment.createdAt).toLocaleString('en-GB')}
                      {' | '}
                      Overall risk: {assessment.overallRisk || 'Not Set'}
                    </Typography>
                    {assessment.lastUpdatedByName && (
                      <Typography variant="caption" color="text.secondary">
                        Last updated by {assessment.lastUpdatedByName}
                        {' - '}
                        {new Date(assessment.updatedAt).toLocaleString('en-GB')}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {canComplete &&
                      String(
                        assessment.completedBy?._id ||
                          assessment.completedBy ||
                          '',
                      ) === String(user?._id || '') && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setEditingAssessment(assessment)
                            setFormOpen(true)
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    <PDFDownloadLink
                      document={
                        <PTSRiskAssessmentPDF
                          assessment={assessment}
                          patient={row.patientRecord}
                        />
                      }
                      fileName={`risk-assessment-${row.patientRecord?.patientID || row.patientId}.pdf`}
                      style={{ textDecoration: 'none' }}
                    >
                      {({ loading: pdfLoading }) => (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PictureAsPdfIcon />}
                          disabled={pdfLoading}
                        >
                          PDF
                        </Button>
                      )}
                    </PDFDownloadLink>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          {canComplete && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingAssessment(null)
                setFormOpen(true)
              }}
            >
              New Assessment
            </Button>
          )}
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const JourneyTable = ({
  rows,
  showWard,
  showRequestedBy,
  showAmount,
  previewOrganisationID,
}) =>
  rows?.length ? (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Booking ID</TableCell>
            {showWard && <TableCell>Ward</TableCell>}
            <TableCell>Patient</TableCell>
            <TableCell>Destination</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Risk Assessments</TableCell>
            {showRequestedBy && <TableCell>Requested By</TableCell>}
            <TableCell>Status</TableCell>
            {showAmount && <TableCell align="right">Amount</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const status = statusStyles[row.status] || statusStyles.Planned
            const bookingChip = getBookingChip(row)
            const trackingChip = getTrackingChip(row)
            return (
              <TableRow
                key={row.journeyId || row.id}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {row.id}
                </TableCell>
                {showWard && <TableCell>{row.ward || '—'}</TableCell>}
                <TableCell>{row.patient}</TableCell>
                <TableCell>{row.destination}</TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight={850}>
                      {formatJourneyTime(
                        row.timeAt || row.plannedAt,
                        row.pickupTime,
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={750}
                      sx={{ lineHeight: 1 }}
                    >
                      {row.timeType || 'Time'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <PortalRiskAssessmentButton
                    row={row}
                    previewOrganisationID={previewOrganisationID}
                  />
                </TableCell>
                {showRequestedBy && <TableCell>{row.requestedBy}</TableCell>}
                <TableCell>
                  <Stack spacing={0.5}>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        width: '100%',
                        bgcolor: status.bg,
                        color: status.color,
                        fontWeight: 900,
                        justifyContent: 'center',
                      }}
                    />
                    {bookingChip && (
                      <Chip
                        label={bookingChip.label}
                        size="small"
                        sx={{
                          width: '100%',
                          bgcolor: bookingChip.bg,
                          color: bookingChip.color,
                          fontWeight: 900,
                          justifyContent: 'center',
                        }}
                      />
                    )}
                    {trackingChip && (
                      <Tooltip title={trackingChip.tooltip} arrow>
                        <Chip
                          label={trackingChip.label}
                          size="small"
                          sx={{
                            width: '100%',
                            bgcolor: trackingChip.bg,
                            color: trackingChip.color,
                            fontWeight: 900,
                            justifyContent: 'center',
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                {showAmount && (
                  <TableCell align="right">{row.amount || '—'}</TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  ) : (
    <Alert severity="info">No journeys to show for this view.</Alert>
  )

const Panel = ({ title, caption, action, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2,
      minHeight: 120,
      border: '1px solid rgba(36,47,64,0.1)',
      bgcolor: '#FFFFFF',
      minWidth: 0,
    }}
  >
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={950}>
            {title}
          </Typography>
          {caption && (
            <Typography variant="body2" color="text.secondary">
              {caption}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Stack>
  </Paper>
)

// Focused default window for journey registers: everything from midnight
// today until at least 12 hours from now (so an evening shift still sees the
// early hours of tomorrow).
const getDefaultJourneyWindow = () => {
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  const endOfDay = new Date(from)
  endOfDay.setDate(endOfDay.getDate() + 1)
  const twelveHoursAhead = new Date(Date.now() + 12 * 60 * 60 * 1000)
  return { from, to: twelveHoursAhead > endOfDay ? twelveHoursAhead : endOfDay }
}

// Journey register with the focused default window and an explicit date-range
// search for older or future work. The range search re-queries the server,
// which scopes every request to the signed-in hospital (and permitted wards),
// so dates can only ever narrow what is visible — never widen it.
const JourneyRegisterPanel = ({
  title,
  caption,
  rows,
  journeysUrl,
  showWard,
  showRequestedBy,
  previewOrganisationID,
}) => {
  const [range, setRange] = useState({ from: '', to: '' })
  const [rangeRows, setRangeRows] = useState(null)
  const [rangeLoading, setRangeLoading] = useState(false)
  const [rangeError, setRangeError] = useState(false)

  const defaultWindow = getDefaultJourneyWindow()
  const windowRows = (rows || []).filter((row) => {
    const time = row?.timeAt || row?.plannedAt
    // Untimed work stays visible — hiding it would lose live journeys
    if (!time) return true
    const at = new Date(time).getTime()
    return at >= defaultWindow.from.getTime() && at < defaultWindow.to.getTime()
  })

  const searchRange = async () => {
    if (!range.from || !range.to || !journeysUrl) return
    setRangeLoading(true)
    setRangeError(false)
    try {
      const res = await API.get(
        `${journeysUrl}&from=${range.from}&to=${range.to}`,
      )
      if (res.status === 200) {
        setRangeRows(res.data?.journeys || [])
      } else {
        setRangeError(true)
      }
    } catch (e) {
      console.error(e)
      setRangeError(true)
    } finally {
      setRangeLoading(false)
    }
  }

  const showingRange = rangeRows !== null

  return (
    <Panel
      title={title}
      caption={
        showingRange
          ? `Journeys between ${range.from} and ${range.to}.`
          : caption
      }
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={range.from}
            onChange={(e) =>
              setRange((prev) => ({ ...prev, from: e.target.value }))
            }
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={range.to}
            onChange={(e) =>
              setRange((prev) => ({ ...prev, to: e.target.value }))
            }
          />
          <Button
            variant="contained"
            size="small"
            disabled={!range.from || !range.to || rangeLoading}
            onClick={searchRange}
            sx={{ fontWeight: 800 }}
          >
            {rangeLoading ? 'Searching...' : 'Search Dates'}
          </Button>
          {showingRange && (
            <Button
              size="small"
              onClick={() => {
                setRangeRows(null)
                setRange({ from: '', to: '' })
                setRangeError(false)
              }}
            >
              Back to Today
            </Button>
          )}
        </Stack>
        {rangeLoading && <LinearProgress />}
        {rangeError && (
          <Alert severity="error">
            Unable to search journeys for that date range.
          </Alert>
        )}
        <JourneyTable
          rows={showingRange ? rangeRows : windowRows}
          showWard={showWard}
          showRequestedBy={showRequestedBy}
          previewOrganisationID={previewOrganisationID}
        />
      </Stack>
    </Panel>
  )
}

const PortalTermsPanel = ({ organisationName, onAccept, accepting }) => {
  const [confirmed, setConfirmed] = useState(false)

  return (
    <Panel
      title="Hospital Portal Terms"
      caption="Please acknowledge these terms before accessing patient transport requests."
    >
      <Stack spacing={2}>
        <Alert severity="info">
          This portal contains patient identifiable information and must only be
          used for legitimate patient transport requests for {organisationName}.
        </Alert>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Stack spacing={1}>
            <Typography fontWeight={900}>By continuing, you agree that:</Typography>
            <Typography variant="body2" color="text.secondary">
              You will only search for and access patient records where you have
              a direct care or transport-request purpose.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You will confirm patient identity using the required matching
              details before creating or viewing transport requests.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Patient information must not be shared outside authorised care
              pathways, and all portal activity may be audited.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Misuse may result in access removal and escalation to your
              organisation.
            </Typography>
          </Stack>
        </Paper>
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
          }
          label="I understand and agree to the hospital booking portal terms."
        />
        <Box>
          <Button
            variant="contained"
            disabled={!confirmed || accepting}
            onClick={onAccept}
            sx={{ fontWeight: 900 }}
          >
            {accepting ? 'Saving...' : 'Accept and continue'}
          </Button>
        </Box>
      </Stack>
    </Panel>
  )
}

const lineItemsTotal = (items, region) =>
  (items || []).reduce(
    (total, item) =>
      total +
      (Number(item.quantity) || 0) *
        (Number(item.unitPrice) || 0) *
        vatMultiplierForItem(item.vat, region),
    0,
  )

// VAT-inclusive gross, net of credit notes and the flat discount - matches
// the server's netAmount maths
const getInvoiceTotal = (invoice, region) => {
  const credited = (invoice?.creditNotes || []).reduce(
    (total, note) => total + lineItemsTotal(note.items, region),
    0,
  )
  return (
    lineItemsTotal(invoice?.items, region) -
    credited -
    Number(invoice?.discount || 0)
  )
}

const PortalInvoicesTable = ({ invoices, loading, currency = '£', error = '' }) => {
  const { tenantInfo } = useContext(TenantSettingsContext)
  const region = tenantInfo?.region

  if (loading) return <LinearProgress />

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (!invoices.length) {
    return (
      <Alert severity="info">
        No approved, sent, or paid invoices are available for this hospital yet.
      </Alert>
    )
  }

  return (
    <Table size="small" sx={{ minWidth: 760 }}>
      <TableHead>
        <TableRow>
          <TableCell>Invoice</TableCell>
          <TableCell>Booking</TableCell>
          <TableCell>Patient</TableCell>
          <TableCell>Date</TableCell>
          <TableCell>Due</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Total</TableCell>
          <TableCell align="right">Paid</TableCell>
          <TableCell align="right">Outstanding</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {invoices.map((invoice) => {
          const total = getInvoiceTotal(invoice, region)
          const paid = Number(invoice.totalPaid || 0)
          const patientName = [
            invoice?.ptsBooking?.patient?.demographics?.firstName,
            invoice?.ptsBooking?.patient?.demographics?.lastName,
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <TableRow key={invoice._id}>
              <TableCell sx={{ fontWeight: 800 }}>{invoice.reference}</TableCell>
              <TableCell>{invoice?.ptsBooking?.bookingRef || '—'}</TableCell>
              <TableCell>
                {patientName || invoice?.ptsBooking?.patient?.patientID || '—'}
              </TableCell>
              <TableCell>
                {invoice.invoiceDate
                  ? new Date(invoice.invoiceDate).toLocaleDateString('en-GB')
                  : '—'}
              </TableCell>
              <TableCell>
                {invoice.dueDate
                  ? new Date(invoice.dueDate).toLocaleDateString('en-GB')
                  : '—'}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={invoice.status || 'Approved'}
                  color={invoice.status === 'Paid' ? 'success' : 'primary'}
                />
              </TableCell>
              <TableCell align="right">
                {currency}
                {total.toFixed(2)}
              </TableCell>
              <TableCell align="right">
                {currency}
                {paid.toFixed(2)}
              </TableCell>
              <TableCell align="right">
                {currency}
                {Math.max(0, total - paid).toFixed(2)}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

const getQuoteTotal = (quote, region) =>
  (quote?.lines || []).reduce(
    (total, line) =>
      total +
      (Number(line.quantity) || 0) *
        (Number(line.unitPrice) || 0) *
        vatMultiplierForItem(line.vat, region),
    0,
  )

const QUOTE_STATUS_COLORS = {
  Sent: 'primary',
  Accepted: 'success',
  Declined: 'error',
  Expired: 'warning',
  Converted: 'success',
}

// Quotes awaiting review/acceptance — Site/Admin users only, like invoices.
// Accepting schedules the booking with the transport provider.
const PortalQuotesTable = ({
  quotes,
  loading,
  currency = '£',
  onAccept,
  previewMode = false,
  error = '',
}) => {
  const { tenantInfo } = useContext(TenantSettingsContext)
  const region = tenantInfo?.region
  const [confirmQuote, setConfirmQuote] = useState(null)
  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState('')

  if (loading) return <LinearProgress />

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (!quotes.length) {
    return (
      <Alert severity="info">
        No quotes have been shared with this hospital yet.
      </Alert>
    )
  }

  // Expired quotes aren't actionable - list them last, greyed, so the live
  // quotes stay front and centre
  const orderedQuotes = [
    ...quotes.filter((quote) => quote.status !== 'Expired'),
    ...quotes.filter((quote) => quote.status === 'Expired'),
  ]

  const confirmAccept = async () => {
    if (!confirmQuote || !onAccept) return
    setAccepting(true)
    setAcceptError('')
    const result = await onAccept(confirmQuote)
    setAccepting(false)
    if (result?.error) {
      setAcceptError(result.error)
    } else {
      setConfirmQuote(null)
    }
  }

  return (
    <>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Quote</TableCell>
            <TableCell>Booking</TableCell>
            <TableCell>Patient</TableCell>
            <TableCell>Sent</TableCell>
            <TableCell>Valid Until</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {orderedQuotes.map((quote) => {
            const patientName = [
              quote?.booking?.patient?.demographics?.firstName,
              quote?.booking?.patient?.demographics?.lastName,
            ]
              .filter(Boolean)
              .join(' ')
            return (
              <TableRow
                key={quote._id}
                sx={quote.status === 'Expired' ? { opacity: 0.55 } : undefined}
              >
                <TableCell sx={{ fontWeight: 800 }}>{quote.quoteRef}</TableCell>
                <TableCell>{quote?.booking?.bookingRef || '—'}</TableCell>
                <TableCell>
                  {patientName || quote?.booking?.patient?.patientID || '—'}
                </TableCell>
                <TableCell>
                  {quote.sentAt
                    ? new Date(quote.sentAt).toLocaleDateString('en-GB')
                    : '—'}
                </TableCell>
                <TableCell>
                  {quote.validUntil
                    ? new Date(quote.validUntil).toLocaleDateString('en-GB')
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={quote.status}
                    color={QUOTE_STATUS_COLORS[quote.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  {currency}
                  {getQuoteTotal(quote, region).toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  {quote.status === 'Sent' && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={previewMode}
                      onClick={() => setConfirmQuote(quote)}
                    >
                      Accept
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Dialog
        open={!!confirmQuote}
        onClose={accepting ? null : () => setConfirmQuote(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Accept quote {confirmQuote?.quoteRef}?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} pt={1}>
            <Typography variant="body2">
              Accepting confirms the quoted price of{' '}
              <b>
                {currency}
                {getQuoteTotal(confirmQuote, region).toFixed(2)}
              </b>{' '}
              and asks the transport provider to schedule the booking. Your
              name will be recorded against the acceptance.
            </Typography>
            {confirmQuote?.lines?.map((line, index) => (
              <Typography key={index} variant="caption" color="text.secondary">
                {line.quantity} × {currency}
                {Number(line.unitPrice).toFixed(2)} — {line.description}
                {line.detail ? ` ${line.detail}` : ''}
              </Typography>
            ))}
            {acceptError && <Alert severity="error">{acceptError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmQuote(null)}
            color="inherit"
            disabled={accepting}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmAccept} disabled={accepting}>
            {accepting ? 'Accepting...' : 'Accept Quote'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const ControlRoomHelpPanel = ({
  accent,
  dispatcherStatus,
  dispatcherStatusLoading,
  onStartChat,
  activeChat,
  onOpenChat,
}) => {
  const [chatStarted, setChatStarted] = useState(false)
  const [chatRequestLoading, setChatRequestLoading] = useState(false)
  const dispatcherAvailable = dispatcherStatus?.available

  const handleStartChat = async () => {
    if (!dispatcherAvailable || chatRequestLoading) return

    setChatRequestLoading(true)
    const request = await onStartChat?.()
    setChatStarted(!!request)
    setChatRequestLoading(false)
  }

  return (
    <Panel
      title="Help & Support"
      caption="Message the control room about bookings, delays, or urgent amendments."
    >
      <Stack spacing={2}>
        <Paper elevation={0} sx={{ p: 2, bgcolor: alpha(accent, 0.07) }}>
          <Typography fontWeight={900}>Control room chat</Typography>
        </Paper>
        {!dispatcherStatusLoading && !dispatcherAvailable && (
          <Alert severity="warning">
            No dispatcher is currently available. Please try again shortly or
            use your normal urgent escalation route.
          </Alert>
        )}
        {dispatcherAvailable && (
          <Alert severity="success">A dispatcher is available now.</Alert>
        )}
        {chatStarted && (
          <Alert severity="info">
            Chat request started. A dispatcher is available to respond.
          </Alert>
        )}
        <Button
          variant="contained"
          startIcon={<ChatBubbleOutlineIcon />}
          disabled={(!dispatcherAvailable && !activeChat) || chatRequestLoading}
          onClick={activeChat ? onOpenChat : handleStartChat}
          sx={{ alignSelf: 'flex-start', bgcolor: accent }}
        >
          {activeChat
            ? 'Open Chat'
            : dispatcherAvailable
              ? chatRequestLoading
                ? 'Starting...'
                : 'Start Chat'
              : 'Chat unavailable'}
        </Button>
      </Stack>
    </Panel>
  )
}

const WardDashboard = ({
  accent,
  selectedWardLabel,
  currentNav,
  setCurrentNav,
  journeys,
  canCreateTransportRequest,
  onCreateTransportRequest,
  previewOrganisationID,
}) => {
  const wardRows = journeys.map((row) => ({
    ...row,
    ward: row.ward || selectedWardLabel,
  }))
  const plannedWindowRows = wardRows.filter(
    (row) =>
      isAlwaysVisibleWardStatus(row) ||
      isPlannedWithinNextHours(row, 12) ||
      isCompletedWithinPastHours(row, 2),
  )
  const myRows = wardRows.filter((row) => row.isRequestedByMe)
  const rows =
    currentNav === 'myRequests'
      ? myRows
      : currentNav === 'allJourneys'
        ? wardRows
        : plannedWindowRows

  const upcomingCount = plannedWindowRows.filter((row) =>
    isPlannedWithinNextHours(row, 12),
  ).length
  const completedCount = plannedWindowRows.filter((row) =>
    isCompletedWithinPastHours(row, 2),
  ).length
  const attentionCount = plannedWindowRows.filter(isAlwaysVisibleWardStatus).length

  if (currentNav === 'help') {
    return <ControlRoomHelpPanel accent={accent} />
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <MetricCard
          icon={<DirectionsCarIcon />}
          value={plannedWindowRows.length}
          label="Planned Work"
          caption="Next 12h / past 2h"
          color={accent}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <MetricCard
          icon={<CalendarMonthIcon />}
          value={upcomingCount}
          label="Upcoming"
          caption="Next 12 hours"
          color="#1F66CC"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <MetricCard
          icon={<FactCheckIcon />}
          value={completedCount}
          label="Completed"
          caption="Past 2 hours"
          color="#00964C"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <MetricCard
          icon={<HistoryIcon />}
          value={attentionCount}
          label="In Progress"
          caption="Any time"
          color="#E32525"
        />
      </Grid>
      <Grid item xs={12}>
        <Panel title="Quick Actions">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!canCreateTransportRequest}
              onClick={onCreateTransportRequest}
              sx={{
                bgcolor: accent,
                fontWeight: 800,
                '&.Mui-disabled': {
                  bgcolor: 'grey.300',
                  color: 'text.disabled',
                },
              }}
            >
              New Transport Request
            </Button>
            <Button
              variant="outlined"
              startIcon={<FactCheckIcon />}
              onClick={() => setCurrentNav?.('myRequests')}
            >
              View My Requests
            </Button>
            <Button
              variant="outlined"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={() => setCurrentNav?.('help')}
            >
              Contact Control Room
            </Button>
          </Stack>
        </Panel>
      </Grid>
      <Grid item xs={12}>
        <Panel
          title={
            currentNav === 'myRequests'
              ? 'My Requests'
              : currentNav === 'allJourneys'
                ? 'All Ward Journeys'
                : `Planned Work - ${selectedWardLabel}`
          }
          caption={
            currentNav === 'allJourneys'
              ? 'All journeys for this ward, including those outside the current planned-work window.'
              : 'Planned journeys in the next 12 hours, completed journeys in the past 2 hours, and any active journey regardless of time.'
          }
          action={
            currentNav !== 'allJourneys' ? (
              <Button
                size="small"
                onClick={() => setCurrentNav?.('allJourneys')}
                sx={{
                  color: accent,
                  fontWeight: 900,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                View all
              </Button>
            ) : null
          }
        >
          <JourneyTable
            rows={rows}
            previewOrganisationID={previewOrganisationID}
          />
        </Panel>
      </Grid>
    </Grid>
  )
}

const PortalWardUserDialog = ({ open, user, wards, onClose, onSaved }) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    permittedWards: [],
    canLogIn: true,
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      permittedWards: user?.ptsBookingPortalAccess?.permittedWards || [],
      canLogIn: user?.security?.canLogIn !== false,
    })
  }, [user, open])

  const selectedWards = useMemo(() => {
    const ids = new Set(
      (form.permittedWards || []).map((ward) => ward?._id || ward),
    )
    return wards.filter((ward) => ids.has(ward._id))
  }, [form.permittedWards, wards])

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      permittedWards: selectedWards.map((ward) => ward._id),
      canLogIn: form.canLogIn,
    }

    const res = user?._id
      ? await API.put(`/pts/portal/users/${user._id}`, payload)
      : await API.post('/pts/portal/users', payload)

    setSaving(false)
    if (res.status === 200) {
      onSaved?.()
      onClose?.()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      component="form"
      onSubmit={submit}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{user?._id ? 'Edit Ward User' : 'Add Ward User'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info">
            Site users can add and manage Ward access users only. Site and Admin
            roles must be managed by your PTS provider.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, firstName: event.target.value }))
                }
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, lastName: event.target.value }))
                }
                required
                fullWidth
              />
            </Grid>
          </Grid>
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, email: event.target.value }))
            }
            disabled={!!user?._id}
            required
            fullWidth
          />
          <Autocomplete
            multiple
            options={wards}
            value={selectedWards}
            getOptionLabel={(option) => getWardLabel(option)}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event, value) =>
              setForm((prev) => ({ ...prev, permittedWards: value }))
            }
            renderInput={(params) => (
              <TextField {...params} label="Permitted wards" required />
            )}
          />
          {user?._id && (
            <TextField
              select
              label="Login"
              value={form.canLogIn ? 'Enabled' : 'Disabled'}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  canLogIn: event.target.value === 'Enabled',
                }))
              }
              fullWidth
            >
              <MenuItem value="Enabled">Enabled</MenuItem>
              <MenuItem value="Disabled">Disabled</MenuItem>
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const SiteDashboard = ({
  accent,
  selectedWardLabel,
  currentNav,
  journeys,
  journeysUrl,
  invoices = [],
  invoicesLoading = false,
  invoicesError = '',
  quotes = [],
  quotesLoading = false,
  quotesError = '',
  onAcceptQuote,
  currency = '£',
  wards,
  users,
  onEditUser,
  onAddUser,
  previewMode = false,
  previewOrganisationID,
}) => {
  const { tenantInfo } = useContext(TenantSettingsContext)
  const region = tenantInfo?.region
  const wardCounts = getWardCounts(journeys)

  if (currentNav === 'quotes') {
    const awaiting = quotes.filter((quote) => quote.status === 'Sent')
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<RequestQuoteIcon />}
            value={awaiting.length}
            label="Awaiting Acceptance"
            caption="Sent and within their validity period"
            color={accent}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<CreditCardIcon />}
            value={`${currency}${awaiting
              .reduce((sum, quote) => sum + getQuoteTotal(quote, region), 0)
              .toFixed(2)}`}
            label="Value Awaiting"
            caption="Accepting schedules the booking"
            color="#1F66CC"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<FactCheckIcon />}
            value={quotes.filter((quote) => quote.status === 'Accepted').length}
            label="Accepted"
            caption="Moved to planning"
            color="#00964C"
          />
        </Grid>
        <Grid item xs={12}>
          <Panel
            title="Quotes"
            caption="Transport quotes shared with this hospital. Accepting confirms the price and schedules the booking."
          >
            <PortalQuotesTable
              quotes={quotes}
              loading={quotesLoading}
              currency={currency}
              onAccept={onAcceptQuote}
              previewMode={previewMode}
              error={quotesError}
            />
          </Panel>
        </Grid>
      </Grid>
    )
  }

  if (currentNav === 'invoicing') {
    const total = invoices.reduce((sum, invoice) => sum + getInvoiceTotal(invoice, region), 0)
    const paid = invoices.reduce(
      (sum, invoice) => sum + Number(invoice.totalPaid || 0),
      0,
    )

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<ReceiptLongIcon />}
            value={invoices.length}
            label="Visible Invoices"
            caption="Drafts are excluded"
            color={accent}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<CreditCardIcon />}
            value={`${currency}${total.toFixed(2)}`}
            label="Invoice Value"
            caption={selectedWardLabel}
            color="#1F66CC"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<FactCheckIcon />}
            value={`${currency}${Math.max(0, total - paid).toFixed(2)}`}
            label="Outstanding"
            caption="Not yet paid"
            color="#F28B1F"
          />
        </Grid>
        <Grid item xs={12}>
          <Panel
            title="Invoices"
            caption="Approved, sent, partially paid, paid, and overdue PTS invoices."
          >
            <PortalInvoicesTable
              invoices={invoices}
              loading={invoicesLoading}
              currency={currency}
              error={invoicesError}
            />
          </Panel>
        </Grid>
      </Grid>
    )
  }

  if (currentNav === 'users') {
    return (
      <Panel
        title="Portal Users"
        caption="Add hospital staff and assign ward-level access."
        action={
          previewMode ? null : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddUser}
            >
              Add Staff
            </Button>
          )
        }
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Access</TableCell>
              <TableCell>Wards</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Terms</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((row) => (
              <TableRow
                key={row._id}
                hover
                onClick={() =>
                  previewMode
                    ? null
                    : row?.ptsBookingPortalAccess?.portalAccessLevel === 'Ward'
                      ? onEditUser(row)
                      : null
                }
                sx={{
                  cursor:
                    !previewMode &&
                    row?.ptsBookingPortalAccess?.portalAccessLevel === 'Ward'
                      ? 'pointer'
                      : 'default',
                }}
              >
                <TableCell>
                  {[row.firstName, row.lastName].filter(Boolean).join(' ')}
                </TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>
                  {row?.ptsBookingPortalAccess?.portalAccessLevel || 'Ward'}
                </TableCell>
                <TableCell>
                  {(row?.ptsBookingPortalAccess?.permittedWards || [])
                    .map(getWardLabel)
                    .join(', ') || 'All wards'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      row?.security?.emailConfirmed
                        ? row?.security?.canLogIn === false
                          ? 'Disabled'
                          : 'Verified'
                        : 'Pending'
                    }
                    color={
                      row?.security?.emailConfirmed
                        ? row?.security?.canLogIn === false
                          ? 'default'
                          : 'success'
                        : 'warning'
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatTermsAccepted(row?.ptsBookingPortalAccess)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    )
  }

  if (currentNav === 'wards') {
    return (
      <Panel
        title="All Wards"
        caption="Journey activity by ward for the selected filter."
      >
        <Grid container spacing={1.5}>
          {wards.map((ward) => {
            const counts =
              wardCounts.find((item) => item.ward === getWardLabel(ward)) || {}
            return (
              <Grid item xs={12} md={6} lg={4} key={ward._id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography fontWeight={950}>{getWardLabel(ward)}</Typography>
                  <Stack direction="row" spacing={2} mt={1}>
                    <Typography variant="body2">
                      <strong>{counts.total || 0}</strong> total
                    </Typography>
                    <Typography variant="body2">
                      <strong>{counts.active || 0}</strong> active
                    </Typography>
                    <Typography variant="body2">
                      <strong>{counts.attention || 0}</strong> attention
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            )
          })}
        </Grid>
      </Panel>
    )
  }

  if (currentNav === 'reports') {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Panel
            title="Reports & Analytics"
            caption="Operational performance by ward and journey status."
          >
            <Stack spacing={1.25}>
              {wardCounts.slice(0, 8).map((ward, index) => (
                <Stack
                  key={ward.ward}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography sx={{ width: 140 }} variant="body2">
                    {ward.ward}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 10,
                      borderRadius: 999,
                      bgcolor: '#E2E8F0',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(12, (ward.total / Math.max(1, journeys.length)) * 100)}%`,
                        height: '100%',
                        borderRadius: 999,
                        bgcolor: accent,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={900}>
                    {ward.total}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Panel>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Panel
            title="Helpful Reports"
            caption="These will support site-level operational review."
          >
            <Stack spacing={1}>
              {[
                'Booking demand by ward',
                'Active journeys by status',
                'Completed journeys export',
                'Ward requester activity',
              ].map((label) => (
                <Button
                  key={label}
                  variant="outlined"
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {label}
                </Button>
              ))}
            </Stack>
          </Panel>
        </Grid>
      </Grid>
    )
  }

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<ReceiptLongIcon />}
            value={journeys.length}
            label="Total Bookings"
            caption={selectedWardLabel}
            color={accent}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<DirectionsCarIcon />}
            value={
              journeys.filter(
                (journey) =>
                  !isClosedJourney(journey),
              ).length
            }
            label="Active Bookings"
            caption="Now"
            color="#239AA4"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<CalendarMonthIcon />}
            value={
              journeys.filter((journey) =>
                UPCOMING_STATUSES.includes(journey.status),
              ).length
            }
            label="Upcoming"
            caption="Awaiting crew"
            color="#00964C"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<HistoryIcon />}
            value={
              journeys.filter((journey) =>
                ACTIVE_STATUSES.includes(journey.status),
              ).length
            }
            label="In Progress"
            caption="Crew on the road"
            color="#E32525"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <JourneyRegisterPanel
            title="All Journeys"
            caption="Today's journeys (and the next 12 hours) for the selected ward filter. Use the date search for anything else."
            rows={journeys}
            journeysUrl={journeysUrl}
            showWard
            showRequestedBy
            previewOrganisationID={previewOrganisationID}
          />
        </Grid>
        <Grid item xs={12}>
          <Panel title="Ward Overview" caption="Current activity by ward.">
            <Stack spacing={1.25}>
              {wardCounts.slice(0, 6).map((row) => (
                <Stack
                  key={row.ward}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography sx={{ width: 125 }} variant="body2">
                    {row.ward}
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      height: 9,
                      borderRadius: 999,
                      bgcolor: '#E2E8F0',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.max(12, (row.total / Math.max(1, journeys.length)) * 100)}%`,
                        height: '100%',
                        borderRadius: 999,
                        bgcolor: accent,
                      }}
                    />
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Panel>
        </Grid>
      </Grid>
    </Stack>
  )
}

const AdminDashboard = ({
  accent,
  selectedWardLabel,
  currentNav,
  journeys,
  journeysUrl,
  invoices = [],
  invoicesLoading = false,
  invoicesError = '',
  quotes = [],
  quotesLoading = false,
  quotesError = '',
  onAcceptQuote,
  currency = '£',
  previewMode = false,
  previewOrganisationID,
}) => {
  const { tenantInfo } = useContext(TenantSettingsContext)
  const region = tenantInfo?.region
  const activeJourneys = journeys.filter((journey) => !isClosedJourney(journey))
  const completedJourneys = journeys.filter(
    (journey) => journey.status === 'Completed',
  )
  const statusCounts = journeys.reduce((acc, journey) => {
    acc[journey.status || 'Unknown'] = (acc[journey.status || 'Unknown'] || 0) + 1
    return acc
  }, {})

  if (currentNav === 'quotes') {
    return (
      <Panel
        title="Quotes"
        caption="Transport quotes shared with this hospital. Accepting confirms the price and schedules the booking."
      >
        <PortalQuotesTable
          quotes={quotes}
          loading={quotesLoading}
          currency={currency}
          onAccept={onAcceptQuote}
          previewMode={previewMode}
          error={quotesError}
        />
      </Panel>
    )
  }

  if (currentNav === 'bookings') {
    return (
      <Panel
        title="Bookings"
        caption={`Booking requests and journey records for ${selectedWardLabel}.`}
      >
        <JourneyTable
          rows={journeys}
          showWard
          showRequestedBy
          previewOrganisationID={previewOrganisationID}
        />
      </Panel>
    )
  }

  if (currentNav === 'journeys') {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<DirectionsCarIcon />}
            value={activeJourneys.length}
            label="Active"
            caption={selectedWardLabel}
            color={accent}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<FactCheckIcon />}
            value={completedJourneys.length}
            label="Completed"
            caption={selectedWardLabel}
            color="#00964C"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<HistoryIcon />}
            value={journeys.filter(isAlwaysVisibleWardStatus).length}
            label="Attention"
            caption="In progress or exception"
            color="#E32525"
          />
        </Grid>
        <Grid item xs={12}>
          <JourneyRegisterPanel
            title="Journey Register"
            caption="Today's journeys (and the next 12 hours). Use the date search for anything else."
            rows={journeys}
            journeysUrl={journeysUrl}
            showWard
            showRequestedBy
            previewOrganisationID={previewOrganisationID}
          />
        </Grid>
      </Grid>
    )
  }

  if (currentNav === 'invoicing') {
    const total = invoices.reduce((sum, invoice) => sum + getInvoiceTotal(invoice, region), 0)
    const paid = invoices.reduce(
      (sum, invoice) => sum + Number(invoice.totalPaid || 0),
      0,
    )

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<ReceiptLongIcon />}
            value={invoices.length}
            label="Visible Invoices"
            caption="Drafts are excluded"
            color={accent}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<CreditCardIcon />}
            value={`${currency}${total.toFixed(2)}`}
            label="Invoice Value"
            caption={selectedWardLabel}
            color="#1F66CC"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            icon={<FactCheckIcon />}
            value={`${currency}${Math.max(0, total - paid).toFixed(2)}`}
            label="Outstanding"
            caption="Not yet paid"
            color="#F28B1F"
          />
        </Grid>
        <Grid item xs={12}>
          <Panel
            title="Invoices"
            caption="Approved, sent, partially paid, paid, and overdue PTS invoices."
          >
            <PortalInvoicesTable
              invoices={invoices}
              loading={invoicesLoading}
              currency={currency}
              error={invoicesError}
            />
          </Panel>
        </Grid>
      </Grid>
    )
  }

  if (currentNav === 'reports') {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Panel
            title="Reports & Analytics"
            caption="Finance, booking volume, and journey performance filtered by ward when required."
          >
            <Stack spacing={2}>
              <Box
                sx={{
                  height: 210,
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.08),
                  p: 2,
                }}
              >
                <Typography fontWeight={900}>
                  Bookings and revenue trend
                </Typography>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  spacing={1}
                  height={150}
                  mt={1}
                >
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <Box
                      key={status}
                      sx={{
                        flex: 1,
                        height: `${Math.max(10, (count / Math.max(1, journeys.length)) * 100)}%`,
                        borderRadius: '8px 8px 0 0',
                        bgcolor: accent,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Panel>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Panel title="Finance Overview" caption={selectedWardLabel}>
            <Typography variant="h4" fontWeight={950}>
              {completedJourneys.length}
            </Typography>
            <Typography color="text.secondary">
              completed journeys in scope
            </Typography>
            <Divider />
            <Typography fontWeight={800}>
              Invoice value will appear once finance records are linked.
            </Typography>
          </Panel>
        </Grid>
      </Grid>
    )
  }

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<ReceiptLongIcon />}
            value={journeys.length}
            label="Total Bookings"
            caption="This month"
            color={accent}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<FactCheckIcon />}
            value={
              journeys.filter((journey) => journey.status === 'Completed')
                .length
            }
            label="Completed"
            caption="This month"
            color="#00964C"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<CreditCardIcon />}
            value="Pending"
            label="Invoicing"
            caption="Finance link"
            color="#1F66CC"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            icon={<HistoryIcon />}
            value={activeJourneys.length}
            label="Active"
            caption={selectedWardLabel}
            color="#F28B1F"
          />
        </Grid>
      </Grid>
      <Panel
        title="Recent Bookings"
        caption="Bookings and journeys for the selected ward filter."
      >
        <JourneyTable
          rows={journeys}
          showWard
          showAmount
          previewOrganisationID={previewOrganisationID}
        />
      </Panel>
    </Stack>
  )
}

const HospitalBookingPortal = ({ previewMode = false }) => {
  const { organisationID } = useParams()
  const navigate = useNavigate()
  const { user, setUser } = useContext(UserContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const access = user?.ptsBookingPortalAccess || {}
  const [previewAccessLevel, setPreviewAccessLevel] = useState('Ward')
  const [previewContext, setPreviewContext] = useState(null)
  const wards = previewMode
    ? previewContext?.wards || []
    : access?.permittedWards || []
  const portalAccessLevel = previewMode
    ? previewAccessLevel
    : access.portalAccessLevel || 'Ward'
  const config = roleConfig[portalAccessLevel] || roleConfig.Ward
  const organisationName =
    previewContext?.organisation?.orgName ||
    user?.ptsOrg?.orgName ||
    'Patient Transport'
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const wardOptions = useMemo(
    () =>
      wards
        .map((ward) => ({
          id: ward?._id || ward,
          _id: ward?._id || ward,
          label: getWardLabel(ward),
        }))
        .filter((ward) => ward.id && ward.label),
    [wards],
  )
  const [selectedWard, setSelectedWard] = useState(
    portalAccessLevel === 'Ward' ? wardOptions[0]?.id || 'all' : 'all',
  )
  const [currentNav, setCurrentNav] = useState('dashboard')
  const [portalJourneys, setPortalJourneys] = useState([])
  const [portalJourneysLoading, setPortalJourneysLoading] = useState(false)
  const [portalJourneysError, setPortalJourneysError] = useState(false)
  const [portalInvoices, setPortalInvoices] = useState([])
  const [portalInvoicesLoading, setPortalInvoicesLoading] = useState(false)
  const [portalInvoicesError, setPortalInvoicesError] = useState('')
  const [portalQuotes, setPortalQuotes] = useState([])
  const [portalQuotesLoading, setPortalQuotesLoading] = useState(false)
  const [portalQuotesError, setPortalQuotesError] = useState('')
  const [portalAccessError, setPortalAccessError] = useState(null)
  const [portalWards, setPortalWards] = useState([])
  const [portalUsers, setPortalUsers] = useState([])
  const [portalUserDialogOpen, setPortalUserDialogOpen] = useState(false)
  const [selectedPortalUser, setSelectedPortalUser] = useState(null)
  const [dispatcherStatus, setDispatcherStatus] = useState(null)
  const [dispatcherStatusLoading, setDispatcherStatusLoading] = useState(false)
  const [activePortalChat, setActivePortalChat] = useState(null)
  const [portalChatOpen, setPortalChatOpen] = useState(false)
  const [newRequestOpen, setNewRequestOpen] = useState(false)
  // Bumped every 30s so journey rows (and live crew tracking) stay fresh
  const [journeysRefreshTick, setJourneysRefreshTick] = useState(0)
  const [termsAccepting, setTermsAccepting] = useState(false)
  const [termsError, setTermsError] = useState(false)
  const portalTermsAccepted =
    access?.termsAcceptedVersion === PTS_PORTAL_TERMS_VERSION &&
    !!access?.termsAcceptedAt
  const portalTermsRequired =
    !previewMode && !!user?.ptsOrg && !portalTermsAccepted

  useEffect(() => {
    if (!previewMode || !organisationID) return

    API.get(`/pts/organisations/${organisationID}/portal-preview/context`)
      .then((res) => {
        if (res.status === 200) {
          setPreviewContext(res.data)
          setPortalWards(res.data?.wards || [])
        } else {
          setPortalAccessError('PREVIEW_UNAVAILABLE')
        }
      })
      .catch((e) => {
        console.error(e)
        setPortalAccessError('PREVIEW_UNAVAILABLE')
      })
  }, [previewMode, organisationID])

  useEffect(() => {
    if (portalAccessLevel === 'Ward' && wardOptions.length) {
      const selectedWardIsValid =
        (selectedWard === 'all' && wardOptions.length > 1) ||
        wardOptions.some((ward) => ward.id === selectedWard)
      if (!selectedWardIsValid) {
        setSelectedWard(wardOptions[0].id)
      }
    } else if (previewMode && portalAccessLevel !== 'Ward') {
      setSelectedWard('all')
    }
  }, [portalAccessLevel, previewMode, selectedWard, wardOptions])

  useEffect(() => {
    if (portalTermsRequired) return

    if (previewMode) return

    API.get('/pts/portal/wards')
      .then((res) => {
        if (res.status === 200) {
          setPortalWards(res.data || [])
        }
      })
      .catch((e) => console.error(e))
  }, [portalTermsRequired, previewMode])

  const loadPortalUsers = async () => {
    if (portalTermsRequired || portalAccessLevel !== 'Site') return

    try {
      const res = await API.get(
        previewMode
          ? `/pts/organisations/${organisationID}/portal-users`
          : '/pts/portal/users',
      )
      if (res.status === 200) {
        setPortalUsers(res.data || [])
      }
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    loadPortalUsers()
  }, [portalTermsRequired, portalAccessLevel, previewMode, organisationID])

  useEffect(() => {
    if (portalTermsRequired) {
      setPortalJourneys([])
      setPortalJourneysLoading(false)
      return
    }

    const wardQuery =
      portalAccessLevel === 'Ward' && selectedWard === 'all'
        ? wardOptions.length > 1
          ? 'all'
          : wardOptions[0]?.id
        : selectedWard

    if (portalAccessLevel === 'Ward' && !wardQuery) {
      setPortalJourneys([])
      setPortalAccessError('NO_WARDS_ASSIGNED')
      return
    }

    if (previewMode && !previewContext) return

    // Only show the loading bar on the first load / filter change, not on
    // background refreshes
    if (!journeysRefreshTick) setPortalJourneysLoading(true)
    setPortalJourneysError(false)
    setPortalAccessError(null)
    const journeysUrl = previewMode
      ? `/pts/organisations/${organisationID}/portal-preview/journeys?ward=${
          wardQuery || 'all'
        }&accessLevel=${portalAccessLevel}`
      : `/pts/portal/journeys?ward=${wardQuery || 'all'}`
    API.get(journeysUrl)
      .then((res) => {
        if (res.status === 200) {
          setPortalJourneys(res.data?.journeys || [])
          setPortalAccessError(res.data?.accessError || null)
          if (
            portalAccessLevel === 'Ward' &&
            res.data?.selectedWard &&
            res.data.selectedWard !== selectedWard
          ) {
            setSelectedWard(res.data.selectedWard)
          }
        } else {
          setPortalJourneysError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setPortalJourneysError(true)
        setPortalAccessError(null)
      })
      .finally(() => setPortalJourneysLoading(false))
  }, [
    portalAccessLevel,
    selectedWard,
    wardOptions,
    portalTermsRequired,
    previewMode,
    previewContext,
    organisationID,
    journeysRefreshTick,
  ])

  useEffect(() => {
    if (previewMode || portalTermsRequired) return undefined
    const interval = setInterval(
      () => setJourneysRefreshTick((tick) => tick + 1),
      30000,
    )
    return () => clearInterval(interval)
  }, [previewMode, portalTermsRequired])

  useEffect(() => {
    if (portalTermsRequired || !['Site', 'Admin'].includes(portalAccessLevel)) {
      setPortalInvoices([])
      setPortalInvoicesLoading(false)
      return
    }
    if (previewMode && !previewContext) return

    setPortalInvoicesLoading(true)
    setPortalInvoicesError('')
    const invoicesUrl = previewMode
      ? `/pts/organisations/${organisationID}/portal-preview/invoices`
      : '/pts/portal/invoices'

    API.get(invoicesUrl)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setPortalInvoices(res.data)
        } else {
          setPortalInvoices([])
          setPortalInvoicesError(
            res?.data?.error || 'Unable to load invoices right now.',
          )
        }
      })
      .catch((e) => {
        console.error(e)
        setPortalInvoices([])
        setPortalInvoicesError('Unable to load invoices right now.')
      })
      .finally(() => setPortalInvoicesLoading(false))
  }, [
    portalTermsRequired,
    portalAccessLevel,
    previewMode,
    previewContext,
    organisationID,
  ])

  const loadPortalQuotes = useCallback(() => {
    if (portalTermsRequired || !['Site', 'Admin'].includes(portalAccessLevel)) {
      setPortalQuotes([])
      setPortalQuotesLoading(false)
      return
    }
    if (previewMode && !previewContext) return

    setPortalQuotesLoading(true)
    setPortalQuotesError('')
    const quotesUrl = previewMode
      ? `/pts/organisations/${organisationID}/portal-preview/quotes`
      : '/pts/portal/quotes'

    API.get(quotesUrl)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setPortalQuotes(res.data)
        } else {
          setPortalQuotes([])
          setPortalQuotesError(
            res?.data?.error || 'Unable to load quotes right now.',
          )
        }
      })
      .catch((e) => {
        console.error(e)
        setPortalQuotes([])
        setPortalQuotesError('Unable to load quotes right now.')
      })
      .finally(() => setPortalQuotesLoading(false))
  }, [
    portalTermsRequired,
    portalAccessLevel,
    previewMode,
    previewContext,
    organisationID,
  ])

  useEffect(() => {
    loadPortalQuotes()
  }, [loadPortalQuotes])

  const acceptPortalQuote = async (quote) => {
    if (previewMode) {
      return { error: 'Quote acceptance is disabled in preview mode.' }
    }
    try {
      const res = await API.post(`/pts/portal/quotes/${quote._id}/accept`)
      if (res.status === 200) {
        loadPortalQuotes()
        return { ok: true }
      }
      return {
        error: res.data?.error || 'Unable to accept this quote right now.',
      }
    } catch (e) {
      return {
        error:
          e?.response?.data?.error ||
          'Unable to accept this quote right now.',
      }
    }
  }

  useEffect(() => {
    const loadDispatcherStatus = async (silent = false) => {
      if (!silent) setDispatcherStatusLoading(true)

      try {
        const res = await API.get('/pts/portal/dispatcher-status')
        if (res.status === 200) {
          setDispatcherStatus(res.data)
        }
      } catch (e) {
        console.error(e)
        setDispatcherStatus({ available: false, dispatcherCount: 0 })
      } finally {
        if (!silent) setDispatcherStatusLoading(false)
      }
    }

    loadDispatcherStatus()
    const interval = setInterval(() => loadDispatcherStatus(true), 15000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!activePortalChat?._id) return undefined

    const loadChat = async () => {
      try {
        const res = await API.get(
          `/pts/portal/chat-requests/${activePortalChat._id}`,
        )
        if (res.status === 200) {
          setActivePortalChat(res.data)
        }
      } catch (e) {
        console.error(e)
      }
    }

    loadChat()
    const interval = setInterval(loadChat, 3000)

    return () => clearInterval(interval)
  }, [activePortalChat?._id])

  const selectedWardLabel =
    selectedWard === 'all'
      ? portalAccessLevel === 'Ward'
        ? wardOptions.length > 1
          ? 'All my wards'
          : wardOptions[0]?.label || ''
        : 'All wards'
      : [
          ...wardOptions,
          ...portalWards.map((ward) => ({
            id: ward._id,
            label: getWardLabel(ward),
          })),
        ].find((ward) => ward.id === selectedWard)?.label || 'Ward'
  const visibleWardOptions = useMemo(
    () =>
      portalAccessLevel === 'Ward'
        ? wardOptions
        : portalWards.map((ward) => ({
            ...ward,
            id: ward._id,
            label: getWardLabel(ward),
          })),
    [portalAccessLevel, wardOptions, portalWards],
  )
  const canCreateTransportRequest =
    !previewMode &&
    !portalTermsRequired &&
    (portalAccessLevel !== 'Ward' || wardOptions.length > 0)
  const selectedWardOption = useMemo(() => {
    if (selectedWard === 'all') {
      // With several wards on "All my wards", leave the booking form's ward
      // blank so the user must pick one explicitly
      if (portalAccessLevel === 'Ward') {
        return wardOptions.length === 1 ? wardOptions[0] : null
      }
      // Site/Admin at a single-ward organisation: default to the only ward so
      // the booking form doesn't demand a selection it never renders
      return visibleWardOptions.length === 1 ? visibleWardOptions[0] : null
    }
    return (
      [
        ...portalWards.map((ward) => ({
          ...ward,
          id: ward._id,
          label: getWardLabel(ward),
        })),
        ...wardOptions,
      ].find((ward) => ward.id === selectedWard) || null
    )
  }, [selectedWard, portalAccessLevel, wardOptions, portalWards, visibleWardOptions])

  // Memoised so background polling (dispatcher status / chat) doesn't hand the
  // booking form a fresh context object every tick and wipe in-progress edits
  const portalBookingContext = useMemo(
    () => ({
      organisation: user?.ptsOrg,
      ward: selectedWardOption,
      wardOptions: visibleWardOptions,
      user,
    }),
    [user, selectedWardOption, visibleWardOptions],
  )

  const openNewTransportRequest = () => {
    if (portalTermsRequired) return
    if (!canCreateTransportRequest) return
    setNewRequestOpen(true)
  }

  const acceptPortalTerms = async () => {
    setTermsAccepting(true)
    setTermsError(false)

    try {
      const res = await API.post('/pts/portal/terms/accept', {
        version: PTS_PORTAL_TERMS_VERSION,
      })

      if (res.status === 200 && res.data?.user) {
        setUser(res.data.user)
      } else {
        setTermsError(true)
      }
    } catch (e) {
      console.error(e)
      setTermsError(true)
    } finally {
      setTermsAccepting(false)
    }
  }

  const navItems =
    portalAccessLevel === 'Admin'
      ? [
          { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
          { key: 'bookings', label: 'Bookings', icon: <ReceiptLongIcon /> },
          { key: 'journeys', label: 'Journeys', icon: <DirectionsCarIcon /> },
          { key: 'quotes', label: 'Quotes', icon: <RequestQuoteIcon /> },
          { key: 'invoicing', label: 'Invoicing', icon: <CreditCardIcon /> },
          { key: 'reports', label: 'Reports', icon: <AnalyticsIcon /> },
          { key: 'help', label: 'Help & Support', icon: <HelpOutlineIcon /> },
        ]
      : portalAccessLevel === 'Site'
        ? [
            { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
            { key: 'request', label: 'Request Transport', icon: <AddIcon /> },
            {
              key: 'allJourneys',
              label: 'All Bookings',
              icon: <ReceiptLongIcon />,
            },
            { key: 'quotes', label: 'Quotes', icon: <RequestQuoteIcon /> },
            { key: 'invoicing', label: 'Invoicing', icon: <CreditCardIcon /> },
            { key: 'wards', label: 'All Wards', icon: <LocalHospitalIcon /> },
            { key: 'users', label: 'Portal Users', icon: <PeopleAltIcon /> },
            {
              key: 'reports',
              label: 'Reports & Analytics',
              icon: <AnalyticsIcon />,
            },
            { key: 'help', label: 'Help & Support', icon: <HelpOutlineIcon /> },
          ]
        : [
            { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
            { key: 'request', label: 'Request Transport', icon: <AddIcon /> },
            {
              key: 'myRequests',
              label: 'My Requests',
              icon: <FactCheckIcon />,
            },
            {
              key: 'allJourneys',
              label: 'All Journeys',
              icon: <ReceiptLongIcon />,
            },
            { key: 'help', label: 'Help & Support', icon: <HelpOutlineIcon /> },
          ]

  const handleLogout = async () => {
    await API.delete('/logout')
    setUser({})
  }

  const startPortalChatRequest = async () => {
    if (previewMode) return null
    try {
      const res = await API.post('/pts/portal/chat-requests', {
        ward:
          selectedWard === 'all'
            ? null
            : {
                _id: selectedWard,
                label: selectedWardLabel,
              },
      })
      if (res.status === 201) {
        setActivePortalChat(res.data)
        setPortalChatOpen(true)
        return res.data
      }
      return null
    } catch (e) {
      console.error(e)
      setDispatcherStatus({ available: false, dispatcherCount: 0 })
      return null
    }
  }

  const sendPortalChatMessage = async (message) => {
    if (!activePortalChat?._id) return false

    try {
      const res = await API.post(
        `/pts/portal/chat-requests/${activePortalChat._id}/messages`,
        { message },
      )
      if (res.status === 200) {
        setActivePortalChat(res.data)
        return true
      }
    } catch (e) {
      console.error(e)
    }

    return false
  }

  const renderContent = () => {
    if (portalTermsRequired) {
      return (
        <Stack spacing={2}>
          {termsError && (
            <Alert severity="error">
              Unable to save your terms acknowledgement. Please try again.
            </Alert>
          )}
          <PortalTermsPanel
            organisationName={organisationName}
            onAccept={acceptPortalTerms}
            accepting={termsAccepting}
          />
        </Stack>
      )
    }

    if (currentNav === 'help') {
      return (
        <ControlRoomHelpPanel
          accent={config.accent}
          dispatcherStatus={dispatcherStatus}
          dispatcherStatusLoading={dispatcherStatusLoading}
          onStartChat={startPortalChatRequest}
          activeChat={activePortalChat}
          onOpenChat={() => setPortalChatOpen(true)}
        />
      )
    }

    if (portalAccessError === 'NO_WARDS_ASSIGNED' && currentNav !== 'help') {
      return (
        <Panel
          title="No ward access assigned"
          caption="Your account is set to Ward access, but no wards have been assigned."
        >
          <Alert severity="error" variant="filled">
            You cannot view hospital portal bookings or journeys until your
            account is assigned to at least one ward. Please contact your site
            administrator.
          </Alert>
        </Panel>
      )
    }

    if (portalAccessError === 'PREVIEW_UNAVAILABLE' && currentNav !== 'help') {
      return (
        <Panel
          title="Preview unavailable"
          caption="The hospital portal preview could not be loaded."
        >
          <Alert severity="error" variant="filled">
            Unable to load this hospital portal preview. Please check the
            organisation still exists and that you have PTS coordinator access.
          </Alert>
        </Panel>
      )
    }

    const registerJourneysUrl = previewMode
      ? `/pts/organisations/${organisationID}/portal-preview/journeys?ward=${
          selectedWard || 'all'
        }&accessLevel=${portalAccessLevel}`
      : `/pts/portal/journeys?ward=${selectedWard || 'all'}`

    if (portalAccessLevel === 'Admin') {
      return (
        <AdminDashboard
          accent={config.accent}
          selectedWardLabel={selectedWardLabel}
          currentNav={currentNav}
          journeys={portalJourneys}
          journeysUrl={registerJourneysUrl}
          invoices={portalInvoices}
          invoicesLoading={portalInvoicesLoading}
          invoicesError={portalInvoicesError}
          quotes={portalQuotes}
          quotesLoading={portalQuotesLoading}
          quotesError={portalQuotesError}
          onAcceptQuote={acceptPortalQuote}
          currency={orgSettings?.currency ?? '£'}
          previewMode={previewMode}
          previewOrganisationID={previewMode ? organisationID : null}
        />
      )
    }
    if (portalAccessLevel === 'Site') {
      return (
        <SiteDashboard
          accent={config.accent}
          selectedWardLabel={selectedWardLabel}
          currentNav={currentNav}
          journeys={portalJourneys}
          journeysUrl={registerJourneysUrl}
          invoices={portalInvoices}
          invoicesLoading={portalInvoicesLoading}
          invoicesError={portalInvoicesError}
          quotes={portalQuotes}
          quotesLoading={portalQuotesLoading}
          quotesError={portalQuotesError}
          onAcceptQuote={acceptPortalQuote}
          currency={orgSettings?.currency ?? '£'}
          wards={portalWards}
          users={portalUsers}
          previewMode={previewMode}
          previewOrganisationID={previewMode ? organisationID : null}
          onAddUser={() => {
            setSelectedPortalUser(null)
            setPortalUserDialogOpen(true)
          }}
          onEditUser={(portalUser) => {
            setSelectedPortalUser(portalUser)
            setPortalUserDialogOpen(true)
          }}
        />
      )
    }
    return (
      <WardDashboard
        accent={config.accent}
        selectedWardLabel={selectedWardLabel}
        currentNav={currentNav}
        setCurrentNav={setCurrentNav}
        journeys={portalJourneys}
        canCreateTransportRequest={canCreateTransportRequest}
        onCreateTransportRequest={openNewTransportRequest}
        previewOrganisationID={previewMode ? organisationID : null}
      />
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Box
        sx={{
          height: 8,
          bgcolor: config.accent,
          background: `linear-gradient(90deg, ${config.accentDark}, ${config.accent})`,
        }}
      />
      <Box
        component="header"
        sx={{
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid rgba(36,47,64,0.1)',
          px: { xs: 2, md: 3 },
          py: 1.75,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
            <Box
              component="img"
              src={orgSettings?.logo || ambunetLogo}
              alt="AmbuNet"
              sx={{ width: 46, height: 46, objectFit: 'contain' }}
            />
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={950} lineHeight={1}>
                {orgSettings?.orgName || 'AmbuNet'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Hospital Booking Portal
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Renders null unless this is a demo tenant */}
            <DemoSwitchButton />
            {previewMode && (
              <Select
                size="small"
                value={previewAccessLevel}
                onChange={(event) => {
                  setPreviewAccessLevel(event.target.value)
                  setCurrentNav('dashboard')
                }}
                sx={{
                  minWidth: { xs: 105, sm: 140 },
                  borderRadius: 2,
                  bgcolor: alpha(config.accent, 0.07),
                  fontWeight: 800,
                  '& .MuiSelect-select': { py: 1 },
                }}
              >
                {Object.keys(roleConfig).map((level) => (
                  <MenuItem key={level} value={level}>
                    {level} view
                  </MenuItem>
                ))}
              </Select>
            )}
            <Select
              size="small"
              value={
                portalAccessLevel === 'Ward' && !wardOptions.length
                  ? ''
                  : selectedWard
              }
              onChange={(event) => setSelectedWard(event.target.value)}
              sx={{
                minWidth: { xs: 130, sm: 210 },
                borderRadius: 2,
                bgcolor: alpha(config.accent, 0.07),
                fontWeight: 800,
                '& .MuiSelect-select': { py: 1 },
              }}
            >
              {portalAccessLevel === 'Ward' && !wardOptions.length && (
                <MenuItem value="">No wards assigned</MenuItem>
              )}
              {portalAccessLevel === 'Ward' && wardOptions.length > 1 && (
                <MenuItem value="all">All my wards</MenuItem>
              )}
              {portalAccessLevel !== 'Ward' && (
                <MenuItem value="all">All wards</MenuItem>
              )}
              {visibleWardOptions.map((ward) => (
                <MenuItem key={ward.id} value={ward.id}>
                  {ward.label}
                </MenuItem>
              ))}
            </Select>
            <Avatar
              sx={{
                bgcolor: alpha(config.accent, 0.16),
                color: config.accent,
                fontWeight: 900,
              }}
            >
              {getInitials(user)}
            </Avatar>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={
                previewMode
                  ? () => navigate(`/clients/${organisationID}`)
                  : handleLogout
              }
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
            >
              {previewMode ? 'Back' : 'Sign out'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 86px)' }}>
        <Box
          component="nav"
          sx={{
            width: { xs: 0, md: 216 },
            display: { xs: 'none', md: 'block' },
            borderRight: '1px solid rgba(36,47,64,0.1)',
            bgcolor: '#FFFFFF',
            p: 1.5,
          }}
        >
          <Stack justifyContent="space-between" height="100%">
            <Stack spacing={0.5}>
              {navItems.map((item) => (
                <NavButton
                  key={item.key}
                  item={item}
                  active={currentNav === item.key}
                  accent={config.accent}
                  onClick={() => {
                    if (item.key === 'request') {
                      openNewTransportRequest()
                      return
                    }
                    setCurrentNav(item.key)
                  }}
                />
              ))}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ cursor: 'pointer' }}
              onClick={() =>
                window.open(
                  'https://ambunet.co.uk',
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            >
              <Box component="img" width={30} src={ambunetLogo} />

              <Box
                sx={{
                  fontFamily: 'moonlander',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'text.secondary',
                }}
              >
                <Stack>
                  <Typography fontSize={8} color="secondary" ml={0.4}>
                    Powered by
                  </Typography>
                  <Box display="flex" gap={0}>
                    AmbuNet
                    <Typography
                      fontFamily="arial"
                      fontSize="0.75rem"
                      color="secondary"
                    >
                      &reg;
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Box
          component="main"
          sx={{ flex: 1, p: { xs: 2, md: 3 }, minWidth: 0 }}
        >
          <Stack spacing={2.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid rgba(36,47,64,0.1)',
                background: `linear-gradient(135deg, ${alpha(config.accent, 0.11)}, #FFFFFF 58%)`,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
              >
                <Box>
                  <Typography
                    variant="h4"
                    fontWeight={950}
                    sx={{ color: config.accent }}
                  >
                    {config.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {organisationName} - {selectedWardLabel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.75}>
                    Welcome{userName ? `, ${userName}` : ''}. {config.subtitle}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={!canCreateTransportRequest}
                  onClick={openNewTransportRequest}
                  sx={{
                    bgcolor: config.accent,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 900,
                    '&:hover': { bgcolor: config.accentDark },
                    '&.Mui-disabled': {
                      bgcolor: 'grey.300',
                      color: 'text.disabled',
                    },
                  }}
                >
                  New Transport Request
                </Button>
              </Stack>
            </Paper>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ overflowX: 'auto', pb: 0.5 }}
              >
                {navItems.map((item) => (
                  <Chip
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    clickable
                    onClick={() => {
                      if (item.key === 'request') {
                        openNewTransportRequest()
                        return
                      }
                      setCurrentNav(item.key)
                    }}
                    sx={{
                      flexShrink: 0,
                      bgcolor:
                        currentNav === item.key ? config.accent : '#FFFFFF',
                      color:
                        currentNav === item.key ? '#FFFFFF' : 'text.primary',
                      border: '1px solid rgba(36,47,64,0.1)',
                      fontWeight: 850,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {portalJourneysLoading && <LinearProgress color="primary" />}
            {portalJourneysError && (
              <Alert severity="error">
                Unable to load hospital portal journeys. Please try again.
              </Alert>
            )}
            <Box>{renderContent()}</Box>
          </Stack>
        </Box>
      </Box>
      <UserSessionIdleTimer enabled={!previewMode} />
      <PortalChatDialog
        open={portalChatOpen}
        request={activePortalChat}
        currentRole="Portal"
        title="Control room chat"
        onClose={() => setPortalChatOpen(false)}
        onSend={sendPortalChatMessage}
      />
      <PortalWardUserDialog
        open={portalUserDialogOpen}
        user={selectedPortalUser}
        wards={portalWards}
        onClose={() => setPortalUserDialogOpen(false)}
        onSaved={loadPortalUsers}
      />
      <BigModal open={newRequestOpen} onClose={() => setNewRequestOpen(false)}>
        <NewPTSBooking
          portalMode
          portalContext={portalBookingContext}
          onSubmitted={() => {
            setNewRequestOpen(false)
            setCurrentNav(
              portalAccessLevel === 'Ward'
                ? 'myRequests'
                : portalAccessLevel === 'Site'
                  ? 'allJourneys'
                  : 'bookings',
            )
            const wardQuery =
              portalAccessLevel === 'Ward' && selectedWard === 'all'
                ? wardOptions.length > 1
                  ? 'all'
                  : wardOptions[0]?.id
                : selectedWard

            setPortalJourneysLoading(true)
            API.get(`/pts/portal/journeys?ward=${wardQuery || 'all'}`)
              .then((res) => {
                if (res.status === 200) {
                  setPortalJourneys(res.data?.journeys || [])
                  setPortalAccessError(res.data?.accessError || null)
                }
              })
              .finally(() => setPortalJourneysLoading(false))
          }}
        />
      </BigModal>
    </Box>
  )
}

export default HospitalBookingPortal
